import { useState, useEffect, useMemo } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { SiteContent } from '../content';

type SectionSettings = SiteContent['sections'];

interface NavbarProps {
  siteName: string;
  sections?: SectionSettings;
}

export default function Navbar({ siteName, sections }: NavbarProps) {
  // Build nav items dynamically based on section visibility and custom titles
  const navItems = useMemo(() => {
    const items = [{ id: 'hero', label: 'Home' }];

    if (sections?.rooms?.visible ?? true) {
      items.push({ id: 'rooms', label: sections?.rooms?.navTitle || 'Rooms' });
    }
    if (sections?.outdoor?.visible ?? true) {
      items.push({ id: 'outdoor', label: sections?.outdoor?.navTitle || 'Outdoor' });
    }
    if (sections?.indoor?.visible ?? true) {
      items.push({ id: 'indoor', label: sections?.indoor?.navTitle || 'Indoor' });
    }
    if (sections?.contact?.visible ?? true) {
      items.push({ id: 'contact', label: sections?.contact?.navTitle || 'Contact' });
    }

    return items;
  }, [sections]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'navbar-scrolled' : 'navbar-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <ScrollLink
            to="hero"
            smooth={true}
            duration={800}
            className="cursor-pointer"
          >
            <span className="font-display text-2xl md:text-3xl text-white font-semibold tracking-wide">
              {siteName}
            </span>
          </ScrollLink>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <ScrollLink
                key={item.id}
                to={item.id}
                smooth={true}
                duration={800}
                offset={-80}
                spy={true}
                activeClass="border-b-2 border-white"
                className="text-white text-sm uppercase tracking-wider cursor-pointer
                           hover:opacity-80 transition-opacity pb-1"
              >
                {item.label}
              </ScrollLink>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-milan-black/95 backdrop-blur-sm">
            <div className="px-2 pt-2 pb-4 space-y-1">
              {navItems.map((item) => (
                <ScrollLink
                  key={item.id}
                  to={item.id}
                  smooth={true}
                  duration={800}
                  offset={-80}
                  className="block px-3 py-3 text-white text-sm uppercase tracking-wider
                             hover:bg-white/10 transition-colors cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </ScrollLink>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
